import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Subscription } from 'rxjs';
import { OnboardingService } from 'src/app/feature/onboarding/services/onboarding.service';
import { SessionTimeoutService } from 'src/app/feature/login/services/session-timeout.service';
import { LoginService } from 'src/app/feature/login/services/login.service';
import { SessionService } from 'src/app/feature/home/services/session.service';
import { TradingService } from 'src/app/feature/trading/services/trading.service';
import { ExpiredTokenError } from '../../data/general-data';
import { DialogDefaultsDirective } from '../../directives/dialog-defaults.directive';
import { ErrorModalService } from '../../services/error-modal-service.service';
import { LoadingService } from '../../services/loading.service';
import { SellIntentService } from '../../services/sell-intent.service';
import { UserCapabilitiesService } from '../../services/user-capabilities.service';
import { UnsubscribeService } from '../../services/unsubscribe.service';
import { UserValidationStateStore } from '../../state/user-validation-state.store';
import {
  DEACTIVATE_BALANCE_MESSAGE,
  DEACTIVATE_SUCCESS_MESSAGE,
  DEACTIVATE_SUCCESS_TITLE,
  DeactivateAccountModalVariant,
  DeactivateBalanceWallet,
  deactivateResponseError,
  resolveDeactivateAccountFailure,
  walletsHaveDeactivateBlockingBalance,
} from '../../utils/deactivate-account';

@Component({
  selector: 'app-unsubscribe',
  standalone: true,
  imports: [CommonModule, ButtonModule, DialogModule, DialogDefaultsDirective],
  templateUrl: './unsubscribe.component.html',
  styleUrls: ['./unsubscribe.component.scss'],
})
export class UnsubscribeComponent implements OnDestroy {
  showDialog = false;
  variant: DeactivateAccountModalVariant = 'confirm';
  message = DEACTIVATE_BALANCE_MESSAGE;
  isDeactivating = false;

  readonly successTitle = DEACTIVATE_SUCCESS_TITLE;
  readonly successMessage = DEACTIVATE_SUCCESS_MESSAGE;

  private readonly subscriptions = new Subscription();
  private prepareRequest = 0;
  private balanceBlocksDeactivation = false;

  constructor(
    private unsubscribeService: UnsubscribeService,
    private sessionService: SessionService,
    private loadingService: LoadingService,
    private errorModalService: ErrorModalService,
    private router: Router,
    private loginService: LoginService,
    private tradingService: TradingService,
    private onboardingService: OnboardingService,
    private sessionTimeoutService: SessionTimeoutService,
    private userValidationStateStore: UserValidationStateStore,
    private sellIntent: SellIntentService,
    private capabilitiesService: UserCapabilitiesService,
  ) {
    this.subscriptions.add(
      this.unsubscribeService.isModalVisible$.subscribe((isVisible) => {
        if (!isVisible) {
          this.showDialog = false;
          this.isDeactivating = false;
          return;
        }

        this.prepareDeactivate();
      }),
    );
  }

  get isSuccess(): boolean {
    return this.variant === 'success';
  }

  get isBlocked(): boolean {
    return this.variant === 'blocked';
  }

  prepareDeactivate(): void {
    const requestId = ++this.prepareRequest;
    this.variant = 'confirm';
    this.message = DEACTIVATE_BALANCE_MESSAGE;
    this.balanceBlocksDeactivation = false;
    this.loadingService.show();

    const walletsSub = this.sessionService.getWallets().subscribe({
      next: (response: unknown) => {
        this.loadingService.hide();
        if (requestId !== this.prepareRequest) {
          return;
        }

        const wallets = Array.isArray(response)
          ? (response as DeactivateBalanceWallet[])
          : [];
        this.balanceBlocksDeactivation =
          walletsHaveDeactivateBlockingBalance(wallets);
        this.variant = this.balanceBlocksDeactivation ? 'blocked' : 'confirm';
        this.showDialog = true;
      },
      error: (error: { status?: number; error?: unknown }) => {
        this.loadingService.hide();
        if (requestId !== this.prepareRequest) {
          return;
        }

        this.unsubscribeService.hide();
        this.showDeactivateError(error);
      },
    });
    this.subscriptions.add(walletsSub);
  }

  unsubscribeUser(): void {
    if (this.isDeactivating || this.variant !== 'confirm') {
      return;
    }

    if (this.balanceBlocksDeactivation) {
      this.variant = 'blocked';
      this.message = DEACTIVATE_BALANCE_MESSAGE;
      return;
    }

    this.isDeactivating = true;
    this.loadingService.show();
    const unsubscribeUserSub = this.sessionService.unsubscribeUser().subscribe({
      next: (response: unknown) => {
        this.loadingService.hide();
        this.isDeactivating = false;
        const failureMessage = deactivateResponseError(response);
        if (failureMessage) {
          this.showDialog = false;
          this.unsubscribeService.hide();
          this.errorModalService.show({
            code: 'DEACTIVATE',
            description: 'No se pudo dar de baja la cuenta',
            detail: [failureMessage],
          });
          return;
        }

        this.variant = 'success';
        this.showDialog = true;
      },
      error: (error: { status?: number; error?: unknown }) => {
        this.loadingService.hide();
        this.isDeactivating = false;
        const failure = resolveDeactivateAccountFailure(error);

        if (failure.kind === 'balance-restricted') {
          this.variant = 'blocked';
          this.message = failure.message;
          this.balanceBlocksDeactivation = true;
          this.showDialog = true;
          return;
        }

        this.showDialog = false;
        this.unsubscribeService.hide();
        this.showDeactivateError(error, failure.kind === 'generic' ? failure.message : undefined);
      },
    });
    this.subscriptions.add(unsubscribeUserSub);
  }

  goToSell(): void {
    this.sellIntent.request();
    this.closeModal();
    void this.router.navigate(['/dashboard']);
  }

  acceptSuccess(): void {
    this.sessionService.setSessionToken(null);
    this.sessionService.setSessionRefreshToken(null);
    this.sessionService.setSessionName(null);
    this.loginService.setLoginToken(null);
    this.onboardingService.setOnbToken(null);
    this.tradingService.setOperationToken(null);
    this.sessionTimeoutService.stopCountdown();
    this.userValidationStateStore.clear();
    this.capabilitiesService.clear();
    this.showDialog = false;
    this.unsubscribeService.hide();
    void this.router.navigate(['/login'], { replaceUrl: true });
  }

  closeModal = (): void => {
    if (this.variant === 'success' || !this.showDialog) {
      return;
    }

    this.prepareRequest += 1;
    this.showDialog = false;
    this.unsubscribeService.hide();
  };

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private showDeactivateError(
    error: { status?: number; error?: unknown },
    genericMessage?: string,
  ): void {
    if (error.status === 401) {
      this.errorModalService.show(ExpiredTokenError);
      return;
    }

    if (genericMessage) {
      this.errorModalService.show({
        code: 'DEACTIVATE',
        description: 'No se pudo dar de baja la cuenta',
        detail: [genericMessage],
      });
      return;
    }

    const body = error.error;
    this.errorModalService.show(
      body && typeof body === 'object' ? (body as never) : undefined,
    );
  }
}
